import { difficulties, themes, type Difficulty } from '../../domain/puzzle/catalog';
export interface Settings {
  muted: boolean;
  volume: number;
  overOtherAudio: boolean;
  coordinates: boolean;
  reduced: boolean;
  /** R5 adds 'adaptive': a hidden strength estimate picks the range. */
  difficulty: Difficulty | 'adaptive';
  mode: 'mixed' | 'theme' | 'review' | 'favorites';
  theme: string;
  ratingMin: number;
  ratingMax: number;
  quality: 'curated' | 'all';
  engineProfile: 'casual' | 'strong' | 'best';
  showEvaluation: boolean;
  /** R4: theme chips stay hidden until the tactic is solved unless this is on. */
  themesWhileSolving: boolean;
  /** R4: opt-in soft wrong/solved cues on top of the board sounds (off: board sounds only). */
  feedbackSounds: boolean;
  /** R4: haptic taps where the device supports them. */
  haptics: boolean;
  /** R4: move on by itself shortly after a clean solve. */
  autoNext: boolean;
  /** R5: show the position before the opponent's move, then play that move (like Chess.com). */
  animateSetup: boolean;
  /** R5: on touch, press and hold a square to draw (the Pen tool works either way). */
  holdToDraw: boolean;
  /** R5: the current hidden adaptive range. Worked out from history on launch; never saved. */
  adaptiveRange?: [number, number];
}
export const defaults: Settings = {
  muted: false,
  volume: 0.35,
  overOtherAudio: false,
  coordinates: true,
  reduced: false,
  difficulty: 'normal',
  mode: 'mixed',
  theme: 'fork',
  ratingMin: 700,
  ratingMax: 2400,
  quality: 'curated',
  engineProfile: 'strong',
  showEvaluation: false,
  themesWhileSolving: false,
  feedbackSounds: false,
  haptics: true,
  autoNext: false,
  // Automated browsers start on the final position so existing tests keep their timing;
  // the R5 tests turn it on explicitly.
  animateSetup: typeof navigator === 'undefined' || !navigator.webdriver,
  holdToDraw: true,
};
export function readSettings(): Settings {
  try {
    const v = JSON.parse(localStorage.getItem('aftermove.settings') ?? '{}');
    const result = { ...defaults };
    for (const key of [
      'muted',
      'overOtherAudio',
      'coordinates',
      'reduced',
      'showEvaluation',
      'themesWhileSolving',
      'feedbackSounds',
      'haptics',
      'autoNext',
      'animateSetup',
      'holdToDraw',
    ] as const)
      if (typeof v[key] === 'boolean') result[key] = v[key];
    if (typeof v.volume === 'number' && Number.isFinite(v.volume))
      result.volume = Math.max(0, Math.min(1, v.volume));
    if (
      typeof v.difficulty === 'string' &&
      (Object.hasOwn(difficulties, v.difficulty) || v.difficulty === 'adaptive')
    )
      result.difficulty = v.difficulty;
    if (['mixed', 'theme', 'review'].includes(v.mode)) result.mode = v.mode;
    if (themes.includes(v.theme)) result.theme = v.theme;
    if (['curated', 'all'].includes(v.quality)) result.quality = v.quality;
    if (['casual', 'strong', 'best'].includes(v.engineProfile))
      result.engineProfile = v.engineProfile;
    for (const key of ['ratingMin', 'ratingMax'] as const)
      if (Number.isInteger(v[key]) && v[key] >= 0 && v[key] <= 4000) result[key] = v[key];
    if (result.ratingMin > result.ratingMax) {
      result.ratingMin = defaults.ratingMin;
      result.ratingMax = defaults.ratingMax;
    }
    return result;
  } catch {
    return { ...defaults };
  }
}
export function saveSettings(s: Settings) {
  try {
    const { adaptiveRange: _derived, ...saved } = s;
    localStorage.setItem('aftermove.settings', JSON.stringify(saved));
  } catch {
    /* Memory settings still work. */
  }
}

export function customRangeError(s: Pick<Settings, 'ratingMin' | 'ratingMax'>): string | undefined {
  if ([s.ratingMin, s.ratingMax].some((n) => !Number.isInteger(n) || n < 0 || n > 4000))
    return 'Use whole ratings from 0 to 4000.';
  if (s.ratingMin > s.ratingMax) return 'Maximum rating must be at least the minimum.';
}
